<template>
  <Layout title="Leave Management" description="Manage leave applications, approvals, and calendar">
    <div class="container py-4">
      <div class="overflow-x-auto">
        <div class="d-flex gap-2 mb-4 flex-nowrap" style="min-width: max-content;">
          <button
            class="btn px-3 py-1 rounded-pill fw-semibold"
            :class="tab === 'application' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
            @click="tab = 'application'"
          >
            Application
          </button>

          <button
            class="btn px-3 py-1 rounded-pill fw-semibold"
            :class="tab === 'approval' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
            @click="tab = 'approval'"
          >
            Approval
          </button>
          
          <button
            class="btn px-3 py-1 rounded-pill fw-semibold"
            :class="tab === 'myLeave' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
            @click="tab = 'myLeave'"
          >
            My Leave
          </button>

          <button
            class="btn px-3 py-1 rounded-pill fw-semibold"
            :class="tab === 'calendar' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
            @click="tab = 'calendar'"
          >
            Calendar
          </button>
        </div>
      </div>

      <div v-if="tab === 'application'" class="mt-3">
        <div class="card border-0 shadow-sm rounded-4 p-4 bg-white">
          <div class="mb-4 text-center">
            <h5 class="fw-bold mb-1">Apply for Leave</h5>
            <p class="text-muted small">
              Fill out the form below to submit a new leave request
            </p>
          </div>

          <div class="row g-3">
            <div class="col-md-12">
              <label class="form-label fw-semibold">Leave Type</label>
              <select class="form-select form-select-lg rounded-3" v-model="leaveForm.type">
                <option disabled value="">Select leave type</option>
                <option value="Annual Leave">Annual Leave</option>
                <option value="Medical Leave">Medical Leave</option>
                <option value="Hospitalization Leave">Hospitalization Leave</option>
                <option value="Off In Lieu">Off In-Lieu</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Start Date</label>
              <input
                type="date"
                class="form-control form-control-lg rounded-3"
                v-model="leaveForm.startDate"
              />
            </div>

            <div class="col-md-6">
                <label class="form-label fw-semibold">End Date</label>
                <input
                    type="date"
                    class="form-control form-control-lg rounded-3"
                    v-model="leaveForm.endDate"
                />
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">Reason</label>
              <textarea
                class="form-control rounded-3"
                v-model="leaveForm.reason"
                rows="4"
                placeholder="Please provide a clear reason for your leave request..."
              ></textarea>
            </div>
          </div>

          <div class="mt-4 text-center">
            <button
              class="btn btn-primary px-5 py-2 fw-semibold rounded-pill d-flex align-items-center justify-content-center gap-2"
              style="background: linear-gradient(90deg, #4f46e5, #6d28d9); border: none; min-width: 160px;"
              :disabled="loading"
              @click="submitLeave"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span>{{ loading ? "Submitting..." : "Submit Application" }}</span>
            </button>
          </div>

        </div>
      </div>

      <div v-if="tab === 'approval'" class="mt-4">
        <div
          v-for="(request, index) in leaveRequests"
          :key="index"
          class="card mb-3 border-0 shadow-sm rounded-4"
        >
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="fw-semibold">{{ request.name }}</div>
              <div class="text-muted small">Request Type: {{ request.type }} 
                <br/>From: {{ request.start }} <br/>End: {{ request.end }}<br/>Reason: {{request.reason}}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span
                class="badge px-2 py-1 text-capitalize"
                :class="{
                  'bg-warning text-dark': request.status === 'pending',
                  'bg-success': request.status === 'approved',
                  'bg-danger': request.status === 'rejected'
                }"
              >
                {{ request.status }}
              </span>
              <div v-if="request.status === 'pending'" class="d-flex gap-2">
                <button class="btn btn-outline-success btn-sm rounded-pill" @click="approveLeave(request.id)">Approve</button>
                <button class="btn btn-outline-danger btn-sm rounded-pill" @click="rejectLeave(request.id)">Reject</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="tab === 'myLeave'" class="mt-4">
        <div
          v-for="(request, index) in myLeaves"
          :key="index"
          class="card mb-3 border-0 shadow-sm rounded-4"
        >
          <div class="card-body d-flex justify-content-between align-items-center">
            <div>
              <div class="fw-semibold">{{ request.name }}</div>
              <div class="text-muted small">Request Type: {{ request.type }} 
                <br/>From: {{ request.start }} <br/>End: {{ request.end }}<br/>Reason: {{request.reason}}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span
                class="badge px-2 py-1 text-capitalize"
                :class="{
                  'bg-warning text-dark': request.status === 'pending',
                  'bg-success': request.status === 'approved',
                  'bg-danger': request.status === 'rejected'
                }"
              >
                {{ request.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="tab === 'calendar'" class="mt-4">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="fw-bold">Approved Leaves Calendar</h5>
  </div>
    
    <!-- <input
      type="month"
      v-model="selectedMonth"
      class="form-control"
      style="width: 200px;"
      @change="fetchApprovedLeaves"
    />

  <div v-if="loadingCalendar" class="text-center my-3">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-2">Fetching approved leaves...</p>
  </div> -->

  <!-- Calendar -->
  <div>
    <vue-cal
      style="height: 700px;"
      default-view="month"
      :time="false"
      :disable-views="['day', 'week', 'year']"
      :events="calendarEvents"
      :locale="'en'"
      hide-view-selector
      events-on-month-view="short"
      @view-change="onCalendarViewChange"
    >
      <template v-slot:event="{ event }">
        <div class="custom-event-content">
          <div class="fw-bold">{{ event.title }}</div>
          <div class="small">{{ event.content }}</div>
        </div>
      </template>
    </vue-cal>
  </div>
</div>

    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue';
import { useToast } from 'vue-toastification';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import VueCal from 'vue-cal';
import 'vue-cal/dist/vuecal.css';
import axios from "axios";


export default {
  name: "LeaveManagement",
  components: { Layout, VueCal },
  data() {
    return {
      tab: "application",
      // apiURL: "http://127.0.0.1:5001",
      apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app", // change if needed
      loadingManagerLeaves: false,
      loading: false,
      loadingCalendar: false,
      selectedMonth: null,
      leaveForm: {
        type: "",
        startDate: "",
        endDate: "",
        reason: "",
      },
      leaveRequests: [],
      myLeaves: [],
      calendarEvents: [],
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  async mounted() {
    this.userID = this.$store.getters.getUserId;
    await this.checkAuthState();
    // 💡 Fetch pending leaves for manager on initial load
    await this.fetchManagerPendingLeaves();
    // 💡 Fetch my leaves for the user on initial load
    await this.fetchMyLeaves();
    // Note: Approved leaves for the calendar will be fetched when the user clicks the tab
  },
  watch: {
    // 💡 Watch for changes in the 'tab' and fetch data accordingly
    async tab(newTab) {
      if (newTab === "approval") {
        await this.fetchManagerPendingLeaves();
      } else if (newTab === "myLeave") {
        await this.fetchMyLeaves();
      } else if (newTab === "calendar") {
        // Set the current month when the calendar tab is selected
        const today = new Date();
        this.selectedMonth = today.toISOString().slice(0, 7);
        await this.fetchApprovedLeaves();
      }
    }
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');
      const token = await user.getIdToken();
      return { Authorization: token };
    },
    async checkAuthState() {
      const auth = getAuth();
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const idToken = await user.getIdToken();
              this.token = idToken;
              resolve();
            } catch (error) {
              reject(error);
            }
          } else {
            router.push("/");
            reject(new Error("No user logged in"));
          }
        });
      });
    },
    async onCalendarViewChange(viewInfo) {
      // Get the year and month directly from the start date provided by vue-cal
      const year = viewInfo.startDate.getFullYear();
      const month = viewInfo.startDate.getMonth() + 1; // getMonth() is 0-indexed, so add 1

      // Format the month with a leading zero if it's a single digit
      const formattedMonth = month < 10 ? `0${month}` : `${month}`;

      // Create the "YYYY-MM" string
      const newMonth = `${year}-${formattedMonth}`;

      // Only fetch if the month has truly changed from the current state
      if (this.selectedMonth !== newMonth) {
        console.log("Calendar changed to month:", newMonth);
        this.selectedMonth = newMonth;
        await this.fetchApprovedLeaves();
      }
    },
    async fetchApprovedLeaves() {
      this.loadingCalendar = true;
      try {
        if (this.selectedMonth == null){
          const today = new Date();
          this.selectedMonth = today.toISOString().slice(0, 7);
        }
        console.log("Fetching approved leaves for month:", this.selectedMonth);
        const headers = await this.getAuthHeader();
    
        console.log(this.selectedMonth)
        const { data } = await axios.get(`${this.apiURL}/api/leave/manager/approved`, {
          headers,
          params: { month: this.selectedMonth }
        });

        if (data.code === 200) {
          this.calendarEvents = data.data; // use backend formatted data
          console.log("Calendar events loaded:", this.calendarEvents);
          // this.toast.success(`Fetched ${this.calendarEvents.length} approved leaves`);
        } else {
          this.toast.error(data.message || "Failed to fetch approved leaves");
        }
      } catch (error) {
        console.error("Error fetching approved leaves:", error);
        this.toast.error(error.response?.data?.message || "Server error, please try again later.");
      } finally {
        this.loadingCalendar = false;
      }
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    async submitLeave() {
      if (!this.leaveForm.type || !this.leaveForm.startDate || !this.leaveForm.endDate || !this.leaveForm.reason) {
        this.toast.error("Please fill in all required fields.");
        return;
      }
      if (this.leaveForm.endDate < this.leaveForm.startDate) {
        this.toast.error("End date cannot be before start date.");
        return;
      }
      this.loading = true;
      try {
        const headers = await this.getAuthHeader();
        const payload = {
          type: this.leaveForm.type,
          startDate: this.leaveForm.startDate,
          endDate: this.leaveForm.endDate,
          reason: this.leaveForm.reason
        };
        const { data } = await axios.post(`${this.apiURL}/api/leave/apply`, payload, {
          headers: {
            ...headers,
            "Content-Type": "application/json"
          }
        });
        if (data.code === 200) {
          this.toast.success(
            `Leave submitted! Remaining balance: ${data.data.remainingBalance}`
          );
          this.tab = "myLeave"; // Switch to My Leave tab to see the pending request
        } else {
          this.toast.error(data.message || "Failed to submit leave application.");
        }
      } catch (error) {
        console.error("Error submitting leave:", error);
        this.toast.error(error.response?.data?.message || "Server error, please try again later.");
      } finally {
        this.loading = false;
        this.leaveForm = { type: "", startDate: "", endDate: "", reason: "" };
      }
    },
    async approveLeave(leaveId) {
      try {
        const headers = await this.getAuthHeader();
        const { data } = await axios.post(`${this.apiURL}/api/leave/approve/${leaveId}`, {}, { headers });
        if (data.code === 200) {
          this.toast.success("Leave request approved successfully!");
          await this.fetchManagerPendingLeaves();
          await this.fetchApprovedLeaves(); // Also refresh the calendar
        } else {
          this.toast.error(data.message || "Failed to approve leave request.");
        }
      } catch (error) {
        console.error("Error approving leave:", error);
        this.toast.error(error.response?.data?.message || "Server error occurred.");
      }
    },
    async rejectLeave(leaveId) {
      try {
        const headers = await this.getAuthHeader();
        const { data } = await axios.post(`${this.apiURL}/api/leave/reject/${leaveId}`, {}, { headers });
        if (data.code === 200) {
          this.toast.success("Leave request rejected successfully!");
          await this.fetchManagerPendingLeaves();
        } else {
          this.toast.error(data.message || "Failed to reject leave request.");
        }
      } catch (error) {
        console.error("Error rejecting leave:", error);
        this.toast.error(error.response?.data?.message || "Server error occurred.");
      }
    },
    async fetchManagerPendingLeaves() {
      this.loadingManagerLeaves = true;
      try {
        const headers = await this.getAuthHeader();
        const { data } = await axios.get(`${this.apiURL}/api/leave/manager/pending`, { headers });
        if (data.code === 200) {
          this.leaveRequests = data.data;
          this.toast.success(`Fetched ${data.data.length} pending leaves`);
        } else {
          this.toast.error(data.message || "Failed to fetch pending leaves");
        }
      } catch (error) {
        console.error("Error fetching manager pending leaves:", error);
        this.toast.error(error.response?.data?.message || "Server error, please try again later.");
      } finally {
        this.loadingManagerLeaves = false;
      }
    },
    async fetchMyLeaves() {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.apiURL}/api/leave/my`, { headers });
        this.myLeaves = res.data.data;
      } catch (error) {
        console.error("Error fetching my leaves:", error);
        this.toast.error(error.response?.data?.message || "Server error occurred while fetching your leaves.");
      }
    },
  },
};
</script>

<style scoped>
/* Event type colors - Make sure these target the correct .vuecal__event element */
.vuecal__event.event-annual {
  background-color: #f87171 !important; /* Red */
  color: #fff !important;
  border-radius: 6px;
  padding: 4px 6px;
  /* Add these styles to ensure the event block is visible */
  height: auto;
  min-height: 25px;
}

.vuecal__event.event-medical {
  background-color: #60a5fa !important; /* Blue */
  color: #fff !important;
  border-radius: 6px;
  padding: 4px 6px;
  /* Add these styles to ensure the event block is visible */
  height: auto;
  min-height: 25px;
}

.vuecal__event.event-emergency {
  background-color: #f59e0b !important; /* Amber */
  color: #fff !important;
  border-radius: 6px;
  padding: 4px 6px;
  /* Add these styles to ensure the event block is visible */
  height: auto;
  min-height: 25px;
}

/* Custom styles for the content inside the event box */
.custom-event-content {
  font-size: 13px;
  text-align: left;
  white-space: normal;
  line-height: 1.2; /* Adjust line spacing for better fit */
}

/* Add this to force the titles to show up, even with limited space */
.vuecal__event-title {
  display: block !important;
  font-size: 13px;
}

/* This is often the real problem.
   Force the cell height to be dynamic if you have many events. */
.vuecal__cell-events {
  height: auto;
}
</style>